/**
 * VoiceTrackSplitter — routes a streamed LLM reply into a spoken "voice
 * track" (<voice>...</voice>, injected by the voice_delivery prompt
 * addendum) and the on-screen remainder. Sits AHEAD of the TTS sanitizer
 * machinery; single-use per stream.
 *
 * States: detecting → (think ↔ detecting) → voice → display, with
 * passthrough as the graceful fallback on any head divergence (both
 * consumers get everything — byte-identical to the pre-feature pipeline,
 * which is what makes model non-compliance a per-turn no-op).
 *
 * Invariant: a message must never persist empty. If the voice block never
 * closes, OR it closes with no on-screen body (nothing, or only
 * whitespace, ever reaches display), the accumulated voice content is
 * flushed to display at `finalize()`.
 *
 * `voiceSink` (constructor option, default `'voice'`) redirects in-voice-
 * block content to the `display` field instead of `voice` — used by
 * `createVoiceTrackRouter` when speech output is off but the voice track's
 * text should still be shown on screen rather than dropped.
 *
 * Spec: docs/superpowers/specs/2026-07-28-voice-track-display-speech-split-design.md §4
 */

export interface SplitResult {
  /** Text for the speech consumer (TTS processToken / avatar echo). */
  voice: string
  /** Text for the display consumer (chat message / transcript). */
  display: string
}

const VOICE_OPEN = '<voice>'
const VOICE_CLOSE = '</voice>'
const THINK_OPEN = '<think>'
const THINK_CLOSE = '</think>'

/** Fresh empty result — never share one object instance across calls;
 * callers may hold onto (and in principle mutate) a returned SplitResult. */
function empty(): SplitResult {
  return { voice: '', display: '' }
}

type State = 'detecting' | 'think' | 'voice' | 'display' | 'passthrough'
type VoiceSink = 'voice' | 'display'

/** Longest suffix of `buf` that is a proper prefix of `tag` (else ''). */
function withheldSuffix(buf: string, tag: string): string {
  const max = Math.min(buf.length, tag.length - 1)
  for (let len = max; len > 0; len--) {
    if (buf.endsWith(tag.slice(0, len))) return tag.slice(0, len)
  }
  return ''
}

function both(text: string): SplitResult {
  return { voice: text, display: text }
}

function merge(a: SplitResult, b: SplitResult): SplitResult {
  return { voice: a.voice + b.voice, display: a.display + b.display }
}

export class VoiceTrackSplitter {
  private state: State = 'detecting'
  private head = ''            // detecting: buffered stream head
  private tail = ''            // think/voice: withheld possible close-tag prefix
  private thinkConsumed = false
  private voiceEmitted = ''    // raw voice-block text released so far (for finalize)
  private readonly voiceSink: VoiceSink

  // Display-confirmation buffer: while the voice block is open (with
  // voiceSink 'display') or after it has closed, display-bound text is
  // withheld until it's known to contain more than whitespace — mirroring
  // the "buffer the ambiguous prefix" pattern already used in `detecting`.
  // This is what lets finalize() answer "did display ever get a real body?"
  // and is NOT applied to mirrored (detecting/think/passthrough) emissions,
  // which always reach both consumers immediately, unchanged.
  private displayPending = ''
  private displayConfirmed = false

  constructor(opts?: { voiceSink?: VoiceSink }) {
    this.voiceSink = opts?.voiceSink ?? 'voice'
  }

  push(token: string): SplitResult {
    switch (this.state) {
      case 'passthrough':
        return both(token)
      case 'display':
        return { voice: '', display: this.routeToDisplay(token) }
      case 'voice':
        return this.pushVoice(token)
      case 'think':
        return this.pushThink(token)
      case 'detecting':
        return this.pushDetecting(token)
    }
  }

  /**
   * Flush at stream end. Idempotent — a repeat call (e.g. a caller that
   * fires both an onComplete and an onError for the same stream) returns
   * `{voice: '', display: ''}` rather than re-emitting the same flush.
   */
  finalize(): SplitResult {
    switch (this.state) {
      case 'detecting': {
        const out = both(this.head)
        this.head = ''
        this.state = 'passthrough'
        return out
      }
      case 'think': {
        const out = both(this.tail)
        this.tail = ''
        this.state = 'passthrough'
        return out
      }
      case 'voice': {
        // Unterminated voice block. The withheld `tail` is by construction
        // a proper prefix of `</voice>` — never real content — and is
        // dropped, not flushed.
        this.tail = ''
        this.state = 'display'
        if (this.voiceSink === 'display' && this.displayConfirmed) {
          // Already streamed to display live (voiceSink redirected it);
          // nothing left to flush.
          return empty()
        }
        this.displayPending = ''
        const flushed = this.voiceEmitted
        // Mark the flush consumed so a second finalize() (idempotency —
        // callers may legitimately fire both onComplete and onError for the
        // same stream, e.g. a backend SSE `error` event paired with an
        // immediate `[DONE]`) hits the `displayConfirmed` guard below
        // instead of re-emitting the same text.
        this.displayConfirmed = true
        this.voiceEmitted = ''
        return { voice: '', display: flushed }
      }
      case 'display': {
        if (this.displayConfirmed) return empty()
        // Voice block closed properly but nothing — or only whitespace —
        // ever reached display: fall back to the voice content so the
        // message never persists empty.
        this.displayPending = ''
        const flushed = this.voiceEmitted
        // Same idempotency guard as the 'voice' case above.
        this.displayConfirmed = true
        this.voiceEmitted = ''
        return { voice: '', display: flushed }
      }
      default:
        return empty()
    }
  }

  private pushDetecting(token: string): SplitResult {
    this.head += token
    const t = this.head.replace(/^\s+/, '')
    if (t === '') return empty() // pure whitespace so far — keep buffering

    // Optional single leading think block: content flows to BOTH as today.
    if (!this.thinkConsumed) {
      if (t.length < THINK_OPEN.length && THINK_OPEN.startsWith(t)) return empty()
      if (t.startsWith(THINK_OPEN)) {
        this.thinkConsumed = true
        this.state = 'think'
        // Emit only up to and including '<think>' (plus any leading
        // whitespace); the remainder re-enters pushThink so a close tag
        // arriving in the SAME token is still detected.
        const consumed = this.head.length - t.length + THINK_OPEN.length
        const emitNow = this.head.slice(0, consumed)
        const rest = this.head.slice(consumed)
        this.head = ''
        this.tail = ''
        return merge(both(emitNow), rest ? this.pushThink(rest) : empty())
      }
    }

    if (t.length < VOICE_OPEN.length && VOICE_OPEN.startsWith(t)) return empty()
    if (t.startsWith(VOICE_OPEN)) {
      this.state = 'voice'
      this.head = ''
      this.tail = ''
      // Pre-tag whitespace is dropped; remainder flows through voice logic.
      return this.pushVoice(t.slice(VOICE_OPEN.length))
    }

    // Divergence — graceful fallback, byte-identical to the old pipeline.
    this.state = 'passthrough'
    const out = both(this.head)
    this.head = ''
    return out
  }

  private pushThink(token: string): SplitResult {
    const buf = this.tail + token
    const idx = buf.indexOf(THINK_CLOSE)
    if (idx !== -1) {
      const upto = buf.slice(0, idx + THINK_CLOSE.length)
      this.tail = ''
      this.state = 'detecting'
      const rest = buf.slice(idx + THINK_CLOSE.length)
      // Think content (incl. close tag) flows to both, then the remainder
      // re-enters detection (it may open the voice tag).
      return merge(both(upto), rest ? this.pushDetecting(rest) : empty())
    }
    this.tail = withheldSuffix(buf, THINK_CLOSE)
    const release = buf.slice(0, buf.length - this.tail.length)
    return both(release)
  }

  private pushVoice(token: string): SplitResult {
    const buf = this.tail + token
    const idx = buf.indexOf(VOICE_CLOSE)
    if (idx !== -1) {
      const voicePart = buf.slice(0, idx)
      const rest = buf.slice(idx + VOICE_CLOSE.length)
      this.tail = ''
      this.state = 'display'
      this.voiceEmitted += voicePart
      return this.emitVoiceSegment(voicePart, rest)
    }
    this.tail = withheldSuffix(buf, VOICE_CLOSE)
    const release = buf.slice(0, buf.length - this.tail.length)
    this.voiceEmitted += release
    return this.emitVoiceSegment(release, '')
  }

  /**
   * Route a chunk of just-confirmed voice-block text (`spoken`) plus any
   * text immediately following a just-found close tag (`afterClose`,
   * always display-bound). Default sink (`'voice'`) keeps `spoken` on the
   * voice field, unbuffered — TTS wants it the instant it's confirmed —
   * and routes only `afterClose` through the display-confirmation buffer.
   * `voiceSink: 'display'` instead redirects `spoken` to display too, in
   * stream order, through that same buffer.
   */
  private emitVoiceSegment(spoken: string, afterClose: string): SplitResult {
    if (this.voiceSink === 'display') {
      return { voice: '', display: this.routeToDisplay(spoken + afterClose) }
    }
    return { voice: spoken, display: this.routeToDisplay(afterClose) }
  }

  /**
   * Withhold display-bound text while it's still all-whitespace-so-far
   * (so a bodyless/whitespace-only close doesn't "confirm" a real body);
   * release everything buffered the moment non-whitespace content shows
   * up, and pass content straight through once confirmed. This is the
   * mechanism `finalize()` uses to detect "display never got a real body."
   */
  private routeToDisplay(text: string): string {
    if (this.displayConfirmed) return text
    const combined = this.displayPending + text
    if (combined.trim() === '') {
      this.displayPending = combined
      return ''
    }
    this.displayPending = ''
    this.displayConfirmed = true
    return combined
  }
}

/**
 * Thin adapter wiring a splitter to display/speech callbacks so callers
 * (ChatContainer) stay a two-liner. `speechEnabled=false` still routes
 * display correctly; `voiceFallback` (default `'display'`) decides what
 * happens to the voice track's text in that case:
 * - `'display'` (default): the voice text is shown on screen (in stream
 *   order, ahead of the post-close body) instead of being spoken.
 * - `'drop'`: the voice text is neither spoken nor shown — the original
 *   drop-on-the-floor behavior.
 */
export function createVoiceTrackRouter(opts: {
  onDisplay: (text: string) => void
  onSpeech: (text: string) => void
  speechEnabled: boolean
  voiceFallback?: 'display' | 'drop'
}): { push(token: string): void; finalize(): void } {
  const voiceFallback = opts.voiceFallback ?? 'display'
  const splitter =
    opts.speechEnabled || voiceFallback === 'drop'
      ? new VoiceTrackSplitter()
      : new VoiceTrackSplitter({ voiceSink: 'display' })
  const emit = ({ voice, display }: SplitResult) => {
    if (display) opts.onDisplay(display)
    if (voice && opts.speechEnabled) opts.onSpeech(voice)
  }
  return {
    push(token: string) {
      emit(splitter.push(token))
    },
    finalize() {
      emit(splitter.finalize())
    },
  }
}
